import {
  memo,
  useState,
  useReducer,
  useEffect,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from 'react';
import { freqSplit, getCenterWithoutLo } from '../../utils/FreqUtils';
import { updateAnalyzerProperties } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import {
  Button,
  MenuItem,
  Grid,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  FormControl,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';

import FrequencySelect from 'components/FrequencySelect';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import snapshotSettingsReducer from '../analyzers/utils/snapshotSettingsReducer';
import { SnapshotTraceHistoryCombination } from '../analyzers/types';
import ColorPicker from '../windowColors/ColorPicker';
import { LockOpen, LockOutlined } from '@mui/icons-material';

import { getAnalyzerType } from 'utils/analyzerTypes';
import { useIsAdmin } from '../auth/hooks/useIsAdmin';
import { useIsOperator } from '../auth/hooks/useIsOperator';

import { usePermissionedUsers } from 'features/userManagement/hooks/usePermissionedUsers';
import { useAnalyzer } from '../analyzers/api/getAnalyzer';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';

interface Props {
  selectedAnalyzerId: number;
  selectedSnapshot: SnapshotTraceHistoryCombination;
  displayActiveSnapshotSelector?: boolean;
  editDisabled: boolean;
  currentDialog?: string;
  lastSnapshotId?: number;
  setLastSnapshotId?: Dispatch<SetStateAction<number>>;
  newSnapshotId?: number;
  setNewSnapshotId?: Dispatch<SetStateAction<number>>;
}
const AdvancedSnapshotSettings = memo(
  ({
    selectedAnalyzerId,
    selectedSnapshot,
    editDisabled,
    currentDialog,
  }: Props) => {
    const dispatch = useAppDispatch();
    const isAdmin = useIsAdmin();
    const isOperator = useIsOperator();

    const { user } = useAppSelector((state) => state.auth);
    const allUsers = usePermissionedUsers(
      selectedSnapshot.id,
      selectedAnalyzerId
    );

    const { data: selectedAnalyzer } = useAnalyzer({
      analyzerId: selectedAnalyzerId,
    });
    const updateSnapshotMutation = useUpdateSnapshot({});
    const analyzerPropertyConstants = getAnalyzerType(
      selectedAnalyzer?.type || 0
    );

    const [editedSnapshot, dispatchLocal] = useReducer(
      snapshotSettingsReducer,
      selectedSnapshot
    );
    const [snapshotEdited, setSnapshotEdited] = useState(false);

    useEffect(() => {
      if (!selectedSnapshot) return;

      dispatchLocal({
        type: 'snapshot-changed',
        snapshot: selectedSnapshot,
      });
    }, [selectedSnapshot]);

    const handleChange = (event: any) => {
      const { name, value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'property-changed',
        name: name,
        value: value,
      });
    };

    const handleChangeLo = (event: ChangeEvent<HTMLInputElement>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'lo-value-changed',
        value: value,
      });
    };

    const handleChangeLoUnit = (event: SelectChangeEvent<string>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'lo-unit-changed',
        value: value,
      });
    };

    const handleChangeLoMode = (event: any) => {
      const { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'lo-mode-changed',
        value: value,
      });
    };

    const handleChangeInverted = (event: any, checked: boolean) => {
      setSnapshotEdited(true);
      dispatchLocal({ type: 'inverted-changed', value: checked });
    };

    const handleLock = () => {
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'property-changed',
        name: 'lock_user_id',
        value: editedSnapshot.lock_user_id ? 0 : user?.id,
      });
    };

    const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (!snapshotEdited) return;

      const centerWithoutLo = getCenterWithoutLo(
        editedSnapshot.center,
        editedSnapshot.lo
      );

      const editedSnapshotWithoutLo = {
        ...editedSnapshot,
        center: centerWithoutLo,
        preamp: Number(editedSnapshot.preamp),
        extra_wait_time: Number(editedSnapshot.extra_wait_time),
      };

      const { baseline_trace, ...editedSnapshotWithoutLoOrBaseline } =
        editedSnapshotWithoutLo;

      if (editedSnapshotWithoutLoOrBaseline.lock_user_id === 0)
        editedSnapshotWithoutLoOrBaseline.lock_user_id = null;

      await updateSnapshotMutation.mutateAsync({
        data: editedSnapshotWithoutLoOrBaseline,
        snapshotId: editedSnapshotWithoutLoOrBaseline?.id || -1,
      });

      // Also update accompanying crosspol snapshot, if any, leaving port unchanged
      if (editedSnapshot.crosspol_snapshot_id) {
        await updateSnapshotMutation.mutateAsync({
          data: {
            ...editedSnapshotWithoutLoOrBaseline,
            port: undefined,
            crosspol_snapshot_id: undefined,
          },
          snapshotId: editedSnapshot.crosspol_snapshot_id,
        });
      }

      if (
        user &&
        selectedAnalyzer &&
        editedSnapshot.id === selectedAnalyzer.active_snapshot
      ) {
        dispatch(
          updateAnalyzerProperties({
            user_id: user.id,
            properties: editedSnapshotWithoutLo,
            analyzer: selectedAnalyzer,
          })
        );
      }

      setSnapshotEdited(false);
    };

    return (
      <Grid
        container
        spacing={1}
        style={currentDialog !== 'marker' ? {} : { display: 'none' }}
      >
        {(isAdmin || !editDisabled) && (
          <Grid item container spacing={1} wrap='wrap'>
            <Grid item xs={3} style={{ minWidth: '108px' }}>
              <Button
                type='submit'
                variant='outlined'
                color='primary'
                onClick={handleSubmit}
                disabled={
                  !editedSnapshot?.id ||
                  (!isAdmin && editDisabled) ||
                  !snapshotEdited
                }
                fullWidth
              >
                Submit
              </Button>
            </Grid>
            <Grid item>
              <ColorPicker
                disabled={editDisabled}
                snapshotId={selectedSnapshot.id}
              />
            </Grid>
          </Grid>
        )}

        <Grid item xs={10}>
          <FormControl variant='outlined' size='small' fullWidth>
            <InputLabel id='detection-mode-label'>Det Mode</InputLabel>
            <Select
              labelId='detection-mode-label'
              id='detection_mode'
              name='detection_mode'
              label='Det Mode'
              required
              fullWidth
              value={
                editedSnapshot?.detection_mode === undefined
                  ? ''
                  : editedSnapshot.detection_mode
              }
              onChange={handleChange}
              disabled={!editedSnapshot?.id || editDisabled}
            >
              {analyzerPropertyConstants?.DETECTION_MODE_VALUES.map(
                (value: string, index: number) => (
                  <MenuItem value={index} key={value}>
                    {value}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid container item spacing={1}>
          <Grid item xs={4}>
            <TextField
              variant='outlined'
              required
              fullWidth
              type='number'
              id='lo'
              label='LO'
              name='loValue'
              autoComplete='lo'
              size='small'
              value={
                isNaN(parseFloat(freqSplit(editedSnapshot?.lo)[0]))
                  ? 0
                  : Math.abs(parseFloat(freqSplit(editedSnapshot?.lo)[0]))
              }
              onChange={handleChangeLo}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
          <Grid item xs={2} style={{ minWidth: '108px' }}>
            <FrequencySelect
              selected={freqSplit(editedSnapshot?.lo)[1] || ''}
              name='loUnit'
              onChange={handleChangeLoUnit}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
          <Grid item xs={4} style={{ minWidth: '200px' }}>
            <FormControl variant='outlined' size='small' fullWidth>
              <InputLabel id='lo-mode-label'>LO Mode</InputLabel>
              <Select
                labelId='lo-mode-label'
                id='lo_mode'
                name='lo_mode'
                label='lo mode'
                required
                fullWidth
                value={editedSnapshot?.lo_mode || 0}
                onChange={handleChangeLoMode}
                disabled={!editedSnapshot?.id || editDisabled}
              >
                <MenuItem value={0} key={0}>
                  Add Center
                </MenuItem>
                <MenuItem value={1} key={1}>
                  Sub Center
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid item sm={10} md={6} style={{ minWidth: '8rem' }}>
          <TextField
            variant='outlined'
            required
            fullWidth
            placeholder='Extra Wait Time'
            id='extra_wait_time'
            label='Extra Wait Time'
            name='extra_wait_time'
            size='small'
            InputProps={{
              endAdornment: <InputAdornment position='end'>ms</InputAdornment>,
            }}
            value={editedSnapshot?.extra_wait_time?.toString() || ''}
            onChange={handleChange}
            disabled={!editedSnapshot?.id || editDisabled}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!editedSnapshot?.inverted}
                name='inverted'
                onChange={handleChangeInverted}
                disabled={!editedSnapshot?.id || editDisabled}
              />
            }
            label='Inverted'
          />
        </Grid>
        {isOperator && (
          <>
            <Grid item>
              <IconButton
                onClick={handleLock}
                disabled={!isAdmin && editDisabled}
              >
                {!editedSnapshot?.lock_user_id ? (
                  <LockOpen />
                ) : (
                  <LockOutlined />
                )}
              </IconButton>
            </Grid>
            <Grid item xs={4}>
              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='lock-label'>Locked To User</InputLabel>
                <Select
                  labelId='lock-label'
                  id='lock_user_id'
                  name='lock_user_id'
                  label='Locked To User'
                  fullWidth
                  value={editedSnapshot?.lock_user_id || 0}
                  onChange={handleChange}
                  disabled={
                    !isAdmin &&
                    (!editedSnapshot?.id ||
                      editDisabled ||
                      !editedSnapshot?.lock_user_id)
                  }
                >
                  {allUsers.map((user) => (
                    <MenuItem value={user.id} key={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                  <MenuItem value={0} key={0}>
                    None
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
    );
  }
);

export default AdvancedSnapshotSettings;
